/**
 * Аналитика: Яндекс.Метрика + Top.Mail.Ru (VK Пиксель)
 *
 * Яндекс.Метрика — два счётчика: 101008137 и 100957118
 * Top.Mail.Ru — счётчик: 3746386
 *
 * Использование:
 *   import { ym, tmr } from '@/lib/analytics';
 *   ym('ORDER_CREATED', { order_price: 2500 });
 *   tmr('order_created');
 */

const COUNTERS = [101008137, 100957118] as const;

type YmParams = Record<string, string | number | boolean | undefined>;

/**
 * Отправить цель (reachGoal) во все счётчики Метрики.
 * Безопасно вызывается на сервере (SSR) — просто ничего не делает.
 */
export function ym(goal: string, params?: YmParams): void {
  if (typeof window === 'undefined') return;

  const ymFn = (window as unknown as Record<string, unknown>).ym as
    | ((counterId: number, method: string, goal: string, params?: YmParams) => void)
    | undefined;

  if (typeof ymFn !== 'function') {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[YM mock] reachGoal: ${goal}`, params);
    }
    return;
  }

  for (const id of COUNTERS) {
    try {
      ymFn(id, 'reachGoal', goal, params);
    } catch {
      // Метрика заблокирована — не ломаем приложение
    }
  }
}

// ═══════════════════════════════════════════════════════════════
// Top.Mail.Ru (VK Пиксель) — JavaScript-цели
// ═══════════════════════════════════════════════════════════════

const TMR_ID = '3746386';

/**
 * Отправить JavaScript-цель в Top.Mail.Ru.
 * Безопасно вызывается на сервере (SSR) — просто ничего не делает.
 */
export function tmr(goal: string): void {
  if (typeof window === 'undefined') return;

  const w = window as unknown as { _tmr?: Array<Record<string, string>> };
  w._tmr = w._tmr || [];

  try {
    w._tmr.push({ id: TMR_ID, type: 'reachGoal', goal });
  } catch {
    // Top.Mail.Ru заблокирован — не ломаем приложение
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(`[TMR] reachGoal: ${goal}`);
  }
}

/** Top.Mail.Ru: просмотр товара */
export const tmrProductView = () => tmr('product_view');

/** Top.Mail.Ru: добавление в корзину */
export const tmrAddToCart = () => tmr('add_to_cart');

/** Top.Mail.Ru: корзина открыта */
export const tmrCartOpen = () => tmr('cart_open');

/** Top.Mail.Ru: шаг оформления */
export const tmrCheckoutStep = (step: string) => tmr(`checkout_${step}`);

/** Top.Mail.Ru: применён промокод */
export const tmrPromoApplied = () => tmr('promo_applied');

/** Top.Mail.Ru: выбран адрес */
export const tmrAddressSelected = () => tmr('address_selected');

/** Top.Mail.Ru: заказ создан */
export const tmrOrderCreated = () => tmr('order_created');

/** Top.Mail.Ru: оплата подтверждена */
export const tmrOrderPaid = () => tmr('order_paid');

/** Top.Mail.Ru: открыл модалку авторизации */
export const tmrAuthOpen = () => tmr('auth_open');

/** Top.Mail.Ru: успешная авторизация */
export const tmrAuthSuccess = () => tmr('auth_success');

/** Top.Mail.Ru: регистрация */
export const tmrRegistration = () => tmr('registration');

/** Top.Mail.Ru: клик по телефону */
export const tmrPhoneClick = () => tmr('phone_click');

/** Top.Mail.Ru: клик по соцсети */
export const tmrSocialClick = () => tmr('social_click');

/** Top.Mail.Ru: отправлен отзыв */
export const tmrReviewSubmitted = () => tmr('review_submitted');

// ─── Готовые хелперы для типовых целей ───

/** Пользователь открыл попап товара */
export const ymProductView = (productId: number, productName: string, price: number) =>
  ym('PRODUCT_VIEW', { product_id: productId, product_name: productName, price });

/** Товар добавлен в корзину */
export const ymAddToCart = (productId: number, productName: string, price: number, quantity: number) =>
  ym('ADD_TO_CART', { product_id: productId, product_name: productName, price, quantity });

/** Корзина открыта (начало оформления) */
export const ymCartOpen = () => ym('CART_OPEN');

/** Переход на шаг оформления */
export const ymCheckoutStep = (step: string) =>
  ym('CHECKOUT_STEP', { step });

/** Заказ создан (отправлен на оплату) */
export const ymOrderCreated = (orderId: number, total: number) =>
  ym('ORDER_CREATED', { order_id: orderId, order_price: total });

/** Оплата подтверждена (success page) */
export const ymOrderPaid = (orderId: number, total: number) =>
  ym('ORDER_PAID', { order_id: orderId, order_price: total });

/** Авторизация — открыл модалку */
export const ymAuthOpen = () => ym('AUTH_OPEN');

/** Авторизация — успешный вход */
export const ymAuthSuccess = (isNewUser: boolean) =>
  ym('AUTH_SUCCESS', { is_new_user: isNewUser });

/** Регистрация нового пользователя */
export const ymRegistration = () => ym('REGISTRATION');

/** Применён промокод */
export const ymPromoApplied = (code: string) =>
  ym('PROMO_APPLIED', { promo_code: code });

/** Клик по телефону */
export const ymPhoneClick = () => ym('PHONE_CLICK');

/** Клик по соцсети */
export const ymSocialClick = (network: string) =>
  ym('SOCIAL_CLICK', { network });

/** Выбран адрес доставки */
export const ymAddressSelected = () => ym('ADDRESS_SELECTED');

/** Отправлен отзыв */
export const ymReviewSubmitted = (rating: number) =>
  ym('REVIEW_SUBMITTED', { rating });

/** Просмотр страницы акций */
export const ymPromotionsView = () => ym('PROMOTIONS_VIEW');

/** Просмотр страницы доставки */
export const ymDeliveryPageView = () => ym('DELIVERY_PAGE_VIEW');

/** Просмотр FAQ */
export const ymFaqView = () => ym('FAQ_VIEW');

/** Просмотр страницы вакансий */
export const ymVacanciesView = () => ym('VACANCIES_VIEW');

/** Просмотр страницы "О нас" */
export const ymAboutView = () => ym('ABOUT_VIEW');


/**
 * Получить ClientID Яндекс.Метрики (_ym_uid) из cookie.
 * Передаётся в заказ для серверной привязки конверсии.
 */
export function getYmUid(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)_ym_uid=([^;]+)/);
  return match?.[1] ?? null;
}

// ═══════════════════════════════════════════════════════════════
// VK / Top.Mail.Ru — серверные конверсии
// ═══════════════════════════════════════════════════════════════

/**
 * Захватить rb_clickid из URL (приходит при переходе из VK рекламы).
 * Сохраняет в localStorage для последующей отправки с заказом.
 * Вызывать один раз при загрузке приложения.
 */
export function captureVkClickId(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const clickId = params.get('rb_clickid');
    if (clickId) localStorage.setItem('rb_clickid', clickId);
  } catch { /* storage недоступен */ }
}

/** Получить rb_clickid из localStorage */
export function getVkClickId(): string | null {
  if (typeof window === 'undefined') return null;
  try { return localStorage.getItem('rb_clickid'); } catch { return null; }
}

/**
 * Получить _tmr_lvid (ID посетителя Top.Mail.Ru) из cookie.
 * Ставится пикселем Top.Mail.Ru автоматически.
 */
export function getTmrLvid(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)_tmr_lvid=([^;]+)/);
  return match?.[1] ?? null;
}

/**
 * Синхронизация userid с пикселем Top.Mail.Ru.
 * Нужно для серверных конверсий — связывает посетителя с userid.
 */
export function tmrSetUserId(userId: string): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { _tmr?: Array<Record<string, string>> };
  w._tmr = w._tmr || [];
  try {
    w._tmr.push({ id: TMR_ID, type: 'setUserID', userid: userId });
  } catch { /* пиксель заблокирован */ }
}

// ═══════════════════════════════════════════════════════════════
// Ecommerce dataLayer (Яндекс.Метрика — Электронная коммерция)
// Документация: https://yandex.ru/support/metrica/ecommerce/data-layer.html
// ═══════════════════════════════════════════════════════════════

interface EcomProduct {
  id: string;
  name: string;
  price: number;
  category?: string;
  quantity?: number;
}

/** Безопасный push в dataLayer */
function pushEcommerce(data: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(data);
}

/** Просмотр карточки товара (detail) */
export function ecomDetail(product: EcomProduct): void {
  pushEcommerce({
    ecommerce: {
      detail: {
        products: [{
          id: product.id,
          name: product.name,
          price: product.price,
          category: product.category || '',
        }],
      },
    },
  });
}

/** Добавление товара в корзину (add) */
export function ecomAdd(product: EcomProduct): void {
  pushEcommerce({
    ecommerce: {
      add: {
        products: [{
          id: product.id,
          name: product.name,
          price: product.price,
          category: product.category || '',
          quantity: product.quantity || 1,
        }],
      },
    },
  });
}

/** Удаление товара из корзины (remove) */
export function ecomRemove(product: EcomProduct): void {
  pushEcommerce({
    ecommerce: {
      remove: {
        products: [{
          id: product.id,
          name: product.name,
          price: product.price,
          category: product.category || '',
          quantity: product.quantity || 1,
        }],
      },
    },
  });
}

/** Покупка (purchase) — вызывать при подтверждении оплаты */
export function ecomPurchase(
  orderId: string,
  total: number,
  products: EcomProduct[],
): void {
  pushEcommerce({
    ecommerce: {
      purchase: {
        actionField: {
          id: orderId,
          revenue: total,
        },
        products: products.map((p) => ({
          id: p.id,
          name: p.name,
          price: p.price,
          category: p.category || '',
          quantity: p.quantity || 1,
        })),
      },
    },
  });
}
